/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { checkToken } from 'api/utils';
import { getEstadosGlobales } from 'api/catalogos';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import { getPeriodosDeNominas } from 'api/nominas/periodos/periodosDeNominas';
import { getPeriodosDeNominasDetalles } from 'api/nominas/periodos/periodosDeNominasDetalle';
import { ESTATUS_AUTORIZADO } from 'utils/constants';
import {
  getLiquidacionesConfiguracionesPercepcionesFechasRelacionadas,
  getLiquidacionConfiguracionPercepcionFechaRelacionada,
  postLiquidacionConfiguracionPercepcionFechaRelacionada,
  patchLiquidacionConfiguracionPercepcionFechaRelacionada,
  deleteLiquidacionConfiguracionPercepcionFechaRelacionada,
} from 'api/nominas/liquidaciones/liquidacionesConfiguracionesPercepcionesFechasRelacionadas';

export const liquidacionConfiguracionPercepcionFechaRelacionadaPermission = {
  permissionModel: 'percepcionperiododepagonorecurrente',
  catalogsModels: [],
  tabsModels: [],
};

const LiquidacionConfiguracionPercepcionFechaRelacionada = ({
  permission,
  percepcionRelacionadaId,
  requierePercepcionRelacionada,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [tiposNominasEmpleados, setTiposNominasEmpleados] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [periodosNominas, setPeriodosNominas] = useState([]);
  const [periodosNominasDetalles, setPeriodosNominasDetalles] = useState([]);
  const [periodosFiltered, setPeriodosFiltered] = useState([]);

  const fetchData = async () => {
    if (!percepcionRelacionadaId) return;
    setLoading(true);
    const _data = await getLiquidacionesConfiguracionesPercepcionesFechasRelacionadas({
      percepcion: percepcionRelacionadaId,
    });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _tiposNominasEmpleados,
        _periodosFiscales,
        _periodosNominas,
        _periodosNominasDetalles,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getTiposDeNominasEmpleados({ estados_globales: ESTATUS_AUTORIZADO }),
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getPeriodosDeNominas({ estados_globales: ESTATUS_AUTORIZADO }),
        getPeriodosDeNominasDetalles(),
        percepcionRelacionadaId
          ? getLiquidacionesConfiguracionesPercepcionesFechasRelacionadas({
            percepcion: percepcionRelacionadaId,
          })
          : Promise.resolve([]),
      ]);
      if (mounted) {
        setEstados(_estados);
        setTiposNominasEmpleados(_tiposNominasEmpleados);
        setPeriodosFiscales(_periodosFiscales);
        setPeriodosNominas(_periodosNominas);
        setPeriodosNominasDetalles(_periodosNominasDetalles);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [percepcionRelacionadaId]);

  if (!requierePercepcionRelacionada || !percepcionRelacionadaId) return null;

  const filterPeriodosDetalles = (overrideValues) => {
    const { tipo_de_nomina_empleado, periodo_fiscal } = overrideValues || form.getFieldsValue();
    const _filtrados = periodosNominas.filter(
      (e) => e.tipo_de_nomina_empleado === tipo_de_nomina_empleado
        && e.periodo_fiscal === periodo_fiscal,
    );
    const _detalles = periodosNominasDetalles.filter(
      (e) => _filtrados.some((x) => x.id === e.periodo_de_nomina),
    );
    setPeriodosFiltered(_detalles);
  };

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    form.resetFields();
    setPeriodosFiltered([]);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const item = data.find((d) => d.id === key);
    const _fullData = await getLiquidacionConfiguracionPercepcionFechaRelacionada(item.id);
    if (_fullData) {
      form.resetFields();
      filterPeriodosDetalles(_fullData);
      form.setFieldsValue(formatReceived(_fullData));
      setVisible(true);
    }
    setLoading(false);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        percepcion: percepcionRelacionadaId,
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchLiquidacionConfiguracionPercepcionFechaRelacionada(key, values)
        : postLiquidacionConfiguracionPercepcionFechaRelacionada(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [form]);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteLiquidacionConfiguracionPercepcionFechaRelacionada(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Tipo de Empleado',
      dataIndex: 'tipo_de_nomina_empleado',
      key: 'tipo_de_nomina_empleado',
      width: 300,
      render: (val) => {
        const tipo = tiposNominasEmpleados.find((e) => e.id === val);
        return tipo ? `${tipo.clave} - ${tipo.descripcion}` : '';
      },
    },
    {
      titleText: 'Periodo Fiscal',
      dataIndex: 'periodo_fiscal',
      key: 'periodo_fiscal',
      width: 200,
      render: (val) => {
        const pf = periodosFiscales.find((e) => e.id === val);
        return pf ? `${pf.periodo}` : '';
      },
    },
    {
      titleText: 'Periodo de Nómina',
      dataIndex: 'periodos_de_nomina_detalle',
      key: 'periodos_de_nomina_detalle',
      width: 400,
      render: (val) => {
        const detalle = periodosNominasDetalles.find((e) => e.id === val);
        if (!detalle) return '';
        const periodoNomina = periodosNominas.find((e) => e.id === detalle.periodo_de_nomina);
        return periodoNomina
          ? `${periodoNomina.prefijo_foliador_nomina} (${detalle.fecha_inicial} al ${detalle.fecha_final})`
          : `(${detalle.fecha_inicial} al ${detalle.fecha_final})`;
      },
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion || '',
    },
  ];

  const requiredRule = { required: true, message: 'El campo es requerido' };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          permission={permission}
          hasAllViewPermission
          allowExportPDF={false}
          onClickAdd={onClickAdd}
          onClickEdit={onClickEdit}
          selectedRowKeys={selectedRowKeys}
          onClickDelete={() => setVisibleAlert(true)}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Fecha Fija`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
          width="60%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="fechaRelacionada"
            initialValues={{ estados_globales: 1 }}
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="tipo_de_nomina_empleado"
                  label="Tipo de Empleado"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Select
                    keyLabelRender
                    showSearch
                    labelProp="descripcion"
                    dataSource={tiposNominasEmpleados}
                    onChange={() => {
                      form.resetFields(['periodos_de_nomina_detalle']);
                      filterPeriodosDetalles();
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="periodo_fiscal"
                  label="Periodo Fiscal"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Select
                    dataSource={periodosFiscales}
                    disabled={!!selectedRowKeys.length}
                    onChange={() => {
                      form.resetFields(['periodos_de_nomina_detalle']);
                      filterPeriodosDetalles();
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="periodos_de_nomina_detalle"
                  label="Periodo de Nómina"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Select
                    keyProp="id"
                    disabled={!!selectedRowKeys.length}
                    dataSource={periodosFiltered}
                    render={(value) => `${value?.numero_de_nomina} (${value?.fecha_inicial} al ${value?.fecha_final})`}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    disabled={!selectedRowKeys.length}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="esta Fecha Fija"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default LiquidacionConfiguracionPercepcionFechaRelacionada;
